import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectField } from '@/components/ui/select-field';
import { SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Genre, getUserRelationshipOptions } from '@/domain/clients/client';
import { getDocTypeOptions } from '@/domain/shared';
import { CreateClientDto } from '@/domain/clients/client-schemas';
import { UseFormReturn } from 'react-hook-form';
import DateSelector from '../shared/DateSelector';
import { argStates } from '../collections/arg-states';
import { countries } from '../collections/countries';

type ReferenceNumber = 1 | 2;

interface ReferenceStepProps {
  form: UseFormReturn<CreateClientDto>;
  referenceNumber: ReferenceNumber;
}

export const ReferenceStep = ({ form, referenceNumber }: ReferenceStepProps) => {
  const prefix = `reference${referenceNumber}` as 'reference1' | 'reference2';
  const hasKey = `hasReference${referenceNumber}` as 'hasReference1' | 'hasReference2';
  const hasValue = form.watch(hasKey);

  return (
    <>
      <div className="col-span-full mb-4">
        <FormField
          control={form.control}
          name={hasKey}
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Información de Referencia {referenceNumber}
                </FormLabel>
                <FormDescription>
                  ¿Desea agregar un {referenceNumber === 1 ? 'primer' : 'segundo'} contacto de referencia?
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {hasValue && (
        <>
          <FormField
            control={form.control}
            name={`${prefix}.name`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Apellido, Nombre *</FormLabel>
                <FormControl>
                  <Input placeholder="Pérez, Juan" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.genre`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Género *</FormLabel>
                <SelectField
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="Seleccionar género"
                  triggerRef={field.ref}
                >
                  {Object.entries(Genre).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectField>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.relationship`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Parentesco *</FormLabel>
                <SelectField
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="Seleccionar parentesco"
                  triggerRef={field.ref}
                >
                  {getUserRelationshipOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectField>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.dateOfBirth`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fecha de Nacimiento *</FormLabel>
                <FormControl>
                  <DateSelector
                    initialDate={field.value}
                    onDateSelected={(date: Date) => field.onChange(date)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.email`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="juan@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.phone`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Teléfono de Contacto</FormLabel>
                <FormControl>
                  <Input placeholder="+54 9 11 1234-5678" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.street`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dirección</FormLabel>
                <FormControl>
                  <Input placeholder="Av. Principal 123" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.country`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>País</FormLabel>
                <SelectField
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    if (value !== 'Argentina') {
                      form.setValue(`${prefix}.state` as any, '');
                    }
                  }}
                  placeholder="Seleccionar país"
                  triggerRef={field.ref}
                >
                  {countries.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectField>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.state`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Provincia</FormLabel>
                {form.getValues(`${prefix}.country`) === 'Argentina' ? (
                  <SelectField
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Seleccionar provincia"
                    triggerRef={field.ref}
                  >
                    {argStates.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectField>
                ) : (
                  <Input placeholder="Provincia" {...field} />
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.city`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ciudad</FormLabel>
                <FormControl>
                  <Input placeholder="Ciudad" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`${prefix}.zipCode`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Código Postal</FormLabel>
                <FormControl>
                  <Input placeholder="Código Postal" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </>
  );
};
